import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";

/**
 * Persist form state to localStorage AND (optionally) to the backend so the user can resume
 * after navigation, accidental refresh, browser crash, OR on a different device.
 *
 * Returns { hasDraft, draftMeta, draftId, restore, clear, saveNow, list }.
 *  - hasDraft: true if a stored draft exists for this key
 *  - draftMeta: { savedAt: ISO, step?: number, source: 'local'|'backend' }
 *  - draftId: backend draft id (string) when backend mode is enabled and a draft exists
 *  - restore(): returns the persisted form state to overwrite local form
 *  - clear():   deletes the persisted draft (call this on successful save / discard)
 *  - saveNow(): force an immediate save (e.g., on "Save Draft & Exit" click)
 *  - list():    fetch the user's drafts of this kind from backend (for dashboards)
 *
 * Usage (local only):
 *   const { hasDraft, restore, clear } = useFormDraft("customer-new", form);
 *
 * Usage (with backend sync, cross-device):
 *   const { hasDraft, draftId, restore, clear, saveNow } = useFormDraft("customer-new", form, { step }, {
 *     backend: true, kind: 'customer', meta: { display_name: `${form.first_name} ${form.last_name}` }
 *   });
 */
export function useFormDraft(key, form, extra = {}, options = {}) {
  const { autoSaveMs = 2000, backend = false, kind = null, meta = null } = options;
  const storageKey = `acas:draft:${key}`;
  const [hasDraft, setHasDraft] = useState(false);
  const [draftMeta, setDraftMeta] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const timerRef = useRef(null);
  const lastSerializedRef = useRef(null);
  const inFlightRef = useRef(false);

  // On mount, check for an existing draft (backend first when enabled, fallback to localStorage).
  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Try backend first
      if (backend && kind) {
        try {
          const resp = await api.get(`/drafts/latest?kind=${kind}`);
          if (cancelled) return;
          const d = resp.data;
          if (d && d.payload) {
            const nonEmpty = Object.values(d.payload).some(
              (v) => v !== "" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0)
            );
            if (nonEmpty) {
              setHasDraft(true);
              setDraftMeta({ savedAt: d.updated_at, step: d.step, source: "backend" });
              setDraftId(d.id);
              // Mirror to localStorage as a fast-access cache
              try {
                localStorage.setItem(
                  storageKey,
                  JSON.stringify({ form: d.payload, extra: { step: d.step, ...d.meta }, savedAt: d.updated_at, draftId: d.id })
                );
              } catch (_) {}
              return;
            }
          }
        } catch (_) {
          // Fall through to localStorage on error
        }
      }
      // Fallback to localStorage
      try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (parsed && parsed.form) {
          const nonEmpty = Object.values(parsed.form).some(
            (v) => v !== "" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0)
          );
          if (nonEmpty && !cancelled) {
            setHasDraft(true);
            setDraftMeta({ savedAt: parsed.savedAt, ...parsed.extra, source: "local" });
            if (parsed.draftId) setDraftId(parsed.draftId);
          }
        }
      } catch (_) {
        /* corrupt — ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, backend, kind]);

  // Auto-save every time form/extra changes (debounced).
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const nowIso = new Date().toISOString();
        const payload = JSON.stringify({ form, extra, savedAt: nowIso, draftId });
        if (payload === lastSerializedRef.current) return;
        lastSerializedRef.current = payload;
        const nonEmpty = Object.values(form || {}).some(
          (v) => v !== "" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0)
        );
        if (!nonEmpty) return;

        // Write to localStorage first (fast, offline-safe)
        try {
          localStorage.setItem(storageKey, payload);
        } catch (_) {}

        // Then sync to backend if enabled (best-effort, non-blocking)
        if (backend && kind && !inFlightRef.current) {
          inFlightRef.current = true;
          try {
            const body = {
              id: draftId || undefined,
              kind,
              step: typeof extra?.step === "number" ? extra.step : 0,
              payload: form,
              meta: meta || {},
            };
            const resp = await api.post("/drafts", body);
            if (resp?.data?.id) {
              if (!draftId) setDraftId(resp.data.id);
              // Update localStorage with the assigned draftId
              try {
                const parsed = JSON.parse(payload);
                parsed.draftId = resp.data.id;
                localStorage.setItem(storageKey, JSON.stringify(parsed));
              } catch (_) {}
            }
          } catch (_) {
            /* offline / 401 — local-only persistence still works */
          } finally {
            inFlightRef.current = false;
          }
        }
      } catch (_) {
        /* quota or serialization issue — ignore */
      }
    }, autoSaveMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [form, extra, autoSaveMs, storageKey, backend, kind, meta, draftId]);

  const restore = useCallback(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return { form: parsed.form, extra: parsed.extra };
    } catch (_) {
      return null;
    }
  }, [storageKey]);

  const clear = useCallback(async () => {
    try {
      localStorage.removeItem(storageKey);
    } catch (_) {}
    setHasDraft(false);
    setDraftMeta(null);
    if (backend && draftId) {
      try {
        await api.delete(`/drafts/${draftId}`);
      } catch (_) {
        /* draft might already be gone */
      }
      setDraftId(null);
    }
  }, [storageKey, backend, draftId]);

  const saveNow = useCallback(async () => {
    if (!backend || !kind) return null;
    try {
      const body = {
        id: draftId || undefined,
        kind,
        step: typeof extra?.step === "number" ? extra.step : 0,
        payload: form,
        meta: meta || {},
      };
      const resp = await api.post("/drafts", body);
      if (resp?.data?.id && !draftId) setDraftId(resp.data.id);
      return resp.data;
    } catch (_) {
      return null;
    }
  }, [backend, kind, draftId, form, extra, meta]);

  return { hasDraft, draftMeta, draftId, restore, clear, saveNow };
}

export default useFormDraft;
